import React, { useCallback } from 'react';
import { Route, Switch } from 'react-router-dom';
import Helmet from 'react-helmet';
import loadable from 'react-loadable-visibility/loadable-components';
import { routePatterns } from './lib/routerUtils';
import { LayoutFactory } from './components/Layouts/LayoutFactory';
import { useAppStateContext } from './lib/AppStateProvider';
import { NavBarContainer } from './components/Header/NavBarContainer';
import { ScrollToTop, tealiumClickCustomFunc } from '@dtcm/ui-components';
import { get, canUseDOM } from './lib/getPropertyByPath';
import { getGlobalDictionaryTerm } from '@dtcm/ui-components/dist/utils';
import { SkipContentButtons } from '@dtcm/ui-components';
import { DUBAI_ARABIC_RANGE, DUBAI_LATIN_RANGE, DUBAI_LATIN_EXT_RANGE} from './constants';

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="12.974" height="12.974">
  <path fill="currentColor" stroke="currentColor" d="m11.207,0.707l1.06,1.06l-4.72,4.72l4.72,4.72l-1.06,1.06l-4.72,-4.72l-4.72,4.72l-1.06,-1.06l4.72,-4.72l-4.72,-4.72l1.06,-1.06l4.72,4.72l4.72,-4.72z" />
</svg>;

const FooterContainer: any = loadable(() =>
  import(/* webpackChunkName: "FooterContainer" */ './components/Footer/FooterContainer')
);

const CTAVariationContainer: any = loadable(() =>
  import(/* webpackChunkName: "FooterContainer" */ './components/CTAVariation/CTAVariationContainer')
);


const generateDubaiStyle = () => {
  const subsetArr = [
    { subsetName: 'arabic', unicode: DUBAI_ARABIC_RANGE },
    { subsetName: 'latin', unicode: DUBAI_LATIN_RANGE },
    { subsetName: 'latin-ext', unicode: DUBAI_LATIN_EXT_RANGE }
  ];
  const weightsArray = [
    { weightValue: '300', name: 'light' },
    { weightValue: '400', name: 'regular' },
    { weightValue: '500', name: 'medium' },
    { weightValue: '700', name: 'bold'}
  ];
  let textContent = '';

  subsetArr.forEach((subset) => {
    weightsArray.forEach((weight) => {
      textContent += `
        @font-face {
          font-family: 'Dubai';
          font-display: swap;
          src: url(${process.env.PUBLIC_URL}/assets/fonts/Dubai/subsets/${subset.subsetName}/dubai-${weight.name}.${subset.subsetName}.woff2) format('woff2');
          font-weight: ${weight.weightValue};
          font-style: normal;
          unicode-range: '${subset.unicode}';
        }
      `;
    });
  });
  return textContent;
}

/*
  APP LAYOUT
  This is where the app's HTML "shell" should be defined.

  All routes share this root layout by default,
  but components added to inner placeholders are route-specific.
*/

function Layout() {
  const { appState } = useAppStateContext();
  const sitecoreContext = appState.sitecoreContextData;
  const languageCode: string = get(appState, 'language.languageCode', 'en');
  const alertBanner = get(sitecoreContext, 'configuration.alertBanner');
  const [showAlertBanner, setShowAlertBanner] = React.useState<boolean>(true);
  const isEmptyLayout = get(appState, 'dictionary.viewBag.isEmptyLayout', false);
  const breadcrumb = get(appState, 'sitecoreContextData.breadcrumb', []);
  const canonical = get(appState, 'sitecoreContextData.canonical', '');
  const btnLabel: string = getGlobalDictionaryTerm(sitecoreContext, 'closeAnnouncementBannerLabel') || "Close announcement banner";
  const timing = canUseDOM ? get(window, 'performance.timing', {}) : {};
  const performanceSpeedDetails = () => {
    if (typeof performance !== 'undefined') {
      const { navigationStart, domContentLoadedEventEnd, loadEventEnd } = timing;
      const pageLoadTime = (loadEventEnd - navigationStart) / 1000;
      const domLoadTime = (domContentLoadedEventEnd - navigationStart) / 1000;

      if (pageLoadTime > 0) {
        return {pageLoadTime, domLoadTime};
      } else {
        return {};
      }
    } else {
      return {};
    }
  };

  React.useEffect(() => {
    let eventAct = '';

    if (breadcrumb.length > 1) {
      const [, val] = breadcrumb;
      eventAct = get(val, 'label', '');
    } else if (breadcrumb.length === 1) {
      const [val] = breadcrumb;
      eventAct = get(val, 'label', '');
    }
    setTimeout(() => {
      const {pageLoadTime, domLoadTime} = performanceSpeedDetails() || {};
      if (pageLoadTime && domLoadTime) {
        const { pathname } = new URL(canonical);
        const eventLab = pathname.split('/').slice(2).join('/');
        tealiumClickCustomFunc({
          event_name: 'eventTracker',
          event_cat: 'Modules',
          event_act: eventAct,
          event_lab: eventLab,
          page_load_time: pageLoadTime,
          dom_load_time: domLoadTime,
        },                     {} as any);
      }
    }, 500);

  }, [
    timing!.domContentLoadedEventEnd,
    timing!.navigationStart,
    timing!.loadEventEnd,
    canUseDOM
  ]);

  const renderFontStyle = useCallback(() => {
    return generateDubaiStyle();
  }, [languageCode]);

  return (
    <React.Fragment>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <link
          rel="stylesheet"
          type="text/css"
          href={`${process.env.PUBLIC_URL}/assets/styles/${
            appState.sitecoreContextData.rtl ? 'ar' : 'en'
          }.min.css?v=7385-1`}
        />
        <style id="font-embed-styles">{renderFontStyle()}</style>
      </Helmet>
      <div className="static-content-wrapper">
        <SkipContentButtons />
        <header className="dot-site-header">
          <div id="announcement-banner" />
          {alertBanner && alertBanner.text && showAlertBanner ? (
            <div className={`alertBanner d-print-none theme-contrast ${alertBanner.theme}`}>
              <div className="container">
                <div className="module-group">
                  <div className="alertBanner__row">
                    <div
                      className="alertBanner__text"
                      dangerouslySetInnerHTML={{
                        __html: get(appState, 'sitecoreContextData.configuration.alertBanner.text'),
                      }}
                    />
                    <div className="alertBanner__button">
                      <button onClick={() => setShowAlertBanner(false)} aria-label={btnLabel}>
                        <span className="accessibility-hidden-text">{btnLabel}</span>
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <NavBarContainer />
        </header>
        <CTAVariationContainer />
        <main className="main overflowXClip js--main">
          <Switch>
            {routePatterns.map((routePattern) => (
              <Route key={routePattern} path={routePattern} component={LayoutFactory} />
            ))}
          </Switch>
        </main>
        <div id="event-remind-confirmations-wrapper" data-recaptcha-ignore="true"><span style={{ display: 'none' }}></span></div>
        {!isEmptyLayout && <ScrollToTop offset={100} analytics={get(appState, 'analytics')} />}
        {/*
      We need to provide content for the loading placeholder used for the footer. This ensures
      that the dynamic loading script triggers for the Footer component. Without any content, it
      seems that the IntersectionObserver never intersects with the Footer component and therefore
      the dynamic loading script doesn't load the component.

      NOTE: most components will not need content in order for dynamic loading to work. The Footer
      seems to be more of an edge case.
      */}
        <FooterContainer />
      </div>
    </React.Fragment>
  );
}

export default Layout;
